import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { setGAUserId, clearGAUserId } from '../utils/google_analytics';
import { getCookie } from '../utils/csrf';

const TOKEN_KEY = 'doritostatsToken';
const USER_KEY = 'doritostatsUser';

const AuthContext = createContext();

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
        error: null,
      };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
};

const initialState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: true,
  error: null,
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const ensureCsrfCookie = async () => {
    await fetch('/api/get-csrf-token/', {
      credentials: 'include',
    });
    return getCookie('csrftoken');
  };

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }
      try {
        const response = await fetch('/api/auth/me/', {
          headers: { Authorization: `Token ${token}` },
        });
        if (response.ok) {
          const user = await response.json();
          dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
          setGAUserId(user.id);
        } else {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };
    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });
      const csrfToken = await ensureCsrfCookie();
      const response = await fetch('/api/auth/login/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        const msg = data.error || 'Login failed.';
        dispatch({ type: 'SET_ERROR', payload: msg });
        return { success: false, error: msg };
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user: data.user, token: data.token } });
      setGAUserId(data.user.id);
      return { success: true };
    } catch {
      const msg = 'Login failed. Please try again.';
      dispatch({ type: 'SET_ERROR', payload: msg });
      return { success: false, error: msg };
    }
  };

  const register = async (email, password, passwordConfirm) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });
      const csrfToken = await ensureCsrfCookie();
      const response = await fetch('/api/auth/register/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken || '',
        },
        body: JSON.stringify({ email, password, password_confirm: passwordConfirm }),
      });
      const data = await response.json();
      if (!response.ok) {
        const msg =
          data.email?.[0] ||
          data.password?.[0] ||
          data.password_confirm?.[0] ||
          data.error ||
          'Registration failed.';
        dispatch({ type: 'SET_ERROR', payload: msg });
        return { success: false, error: msg };
      }
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      dispatch({ type: 'LOGIN_SUCCESS', payload: { user: data.user, token: data.token } });
      setGAUserId(data.user.id);
      return { success: true };
    } catch {
      const msg = 'Registration failed. Please try again.';
      dispatch({ type: 'SET_ERROR', payload: msg });
      return { success: false, error: msg };
    }
  };

  const logout = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      const csrfToken = await ensureCsrfCookie();
      fetch('/api/auth/logout/', {
        method: 'POST',
        credentials: 'include',
        headers: {
          Authorization: `Token ${token}`,
          'X-CSRFToken': csrfToken || '',
        },
      }).catch(() => {});
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    clearGAUserId();
    dispatch({ type: 'LOGOUT' });
  };

  const value = {
    ...state,
    login,
    register,
    logout,
    clearError: () => dispatch({ type: 'CLEAR_ERROR' }),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
